import React, { useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import { useSelector } from "react-redux"
import { selectEmployees } from 'src/stores/employeeSlice';
import CentralizedTable from './CentralizedTable';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement,
  LineElement, BarElement, ArcElement, Title, Tooltip as ChartTooltip, Legend, Filler
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { CalendarCheck, PieChart, BarChart3, History, X } from 'lucide-react';
import { Select, MenuItem, TextField, Autocomplete, Skeleton, Tabs, Tab, Button, Tooltip, Chip } from '@mui/material';
import { FilterAltOutlined } from '@mui/icons-material';
import ApiClient from '../ApiClient';
import { notify } from '../utils/ToastUtils';
import { leaveOptions } from '../utils/constants';

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement,
  Title, ChartTooltip, Legend, Filler, ChartDataLabels
);

export default function ManagerLeaveWfhOverview() {
    const employeesList = useSelector(selectEmployees) || [];
    
    // State Modifications
    const [filters, setFilters] = useState({
        startDate: moment().startOf('month').format('YYYY-MM-DD'),
        endDate: moment().format('YYYY-MM-DD'),
        employees: [],
        location: 'All',
        status: 'All',
        requestType: 'All',
        leaveType: 'All'
    });

    const [appliedFilters, setAppliedFilters] = useState({ ...filters });
    const [loading, setLoading] = useState(false);
    const [rawData, setRawData] = useState([]);
    const [activeTab, setActiveTab] = useState('All');

    const selectMenuProps = {
        PaperProps: {
            style: {
                maxHeight: 250,
                maxWidth: 10,
            },
            sx: {
                '& .MuiMenuItem-root': {
                    fontSize: '14px',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                }
            }
        },
    };

    // Filter Handlers
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filters });
    };

    const handleResetFilters = () => {
        const defaultFilters = {
            startDate: moment().startOf('month').format('YYYY-MM-DD'),
            endDate: moment().format('YYYY-MM-DD'),
            employees: [],
            location: 'All',
            status: 'All',
            requestType: 'All',
            leaveType: 'All'
        };
        setFilters(defaultFilters);
        setAppliedFilters(defaultFilters);
    };

    // Data Fetching via ApiClient
    const getDashboardData = async () => {
        setLoading(true);
        try {
            const empCodes = appliedFilters.employees.length > 0 ? appliedFilters.employees.map(e => e.EMP_CODE) : "All";
            
            const [leaveRes, wfhRes] = await Promise.all([
                ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/getLeaveRecords`, {
                    startDt: appliedFilters.startDate,
                    endDt: appliedFilters.endDate,
                    code: empCodes,
                    leaveType: appliedFilters.leaveType
                }),
                ApiClient.post(`${process.env.REACT_APP_BASE_URL}/admin/getWfhRecords`, {
                    startDt: appliedFilters.startDate,
                    endDt: appliedFilters.endDate,
                    code: empCodes
                }).catch(e => {
                    console.warn("WFH fetch failed or endpoint doesn't exist", e);
                    return { data: [] };
                })
            ]);
            
            const leaves = (leaveRes.data || []).map((l, idx) => ({ ...l, id: `leave-${idx}-${l.EMP_CODE || ''}`, _reqType: 'Leave' }));
            const wfhs = (wfhRes.data || []).map((w, idx) => ({ 
                ...w, 
                id: `wfh-${idx}-${w.EMP_CODE || ''}`,
                _reqType: 'WFH', 
                LEAVE_TYPE: '-' 
            }));
            
            setRawData([...leaves, ...wfhs]);
        } catch (error) {
            console.error(error);
            notify.error("Failed to load records");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appliedFilters]);

    // Data Normalization and Local Filtering
    const baseFilteredData = useMemo(() => {
        let data = [...rawData];

        // Filter by Employees from Redux Scope
        const empCodeMap = new Set(employeesList.map(e => e.EMP_CODE));
        data = data.filter(r => empCodeMap.has(r.EMP_CODE));

        if (appliedFilters.employees.length > 0) {
            const selectedCodes = new Set(appliedFilters.employees.map(e => e.EMP_CODE));
            data = data.filter(r => selectedCodes.has(r.EMP_CODE));
        }
        
        if (appliedFilters.location !== 'All') {
            data = data.filter(r => {
                const emp = employeesList.find(e => e.EMP_CODE === r.EMP_CODE);
                return emp && emp.LOCATION === appliedFilters.location;
            });
        }

        if (appliedFilters.status !== 'All') {
            data = data.filter(r => {
                let status = r.LEAVE_STATUS || r.STATUS || "Pending";
                if (status === 'Accepted') status = 'Approved';
                return status === appliedFilters.status;
            });
        }

        if (appliedFilters.requestType !== 'All') {
            data = data.filter(r => r._reqType === appliedFilters.requestType);
        }

        if (appliedFilters.leaveType !== 'All') {
            data = data.filter(r => r.LEAVE_TYPE === appliedFilters.leaveType);
        }

        return data;
    }, [rawData, employeesList, appliedFilters]);


    // Table Data (Applies Active Tab)
    const tableData = useMemo(() => {
        if (activeTab === 'All') return baseFilteredData;
        return baseFilteredData.filter(r => r._reqType === activeTab);
    }, [baseFilteredData, activeTab]);

    // Charts Data
    const leaveCount = baseFilteredData.filter(d => d._reqType === 'Leave').length;
    const wfhCount = baseFilteredData.filter(d => d._reqType === 'WFH').length;
    const totalReqs = leaveCount + wfhCount;

    const doughnutData = {
        labels: ['Leave', 'WFH'],
        datasets: [{
            data: [leaveCount, wfhCount],
            backgroundColor: ['#3B82F6', '#22C55E'],
            borderWidth: 0,
            hoverOffset: 4
        }]
    };

    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '75%',
        layout: {
            padding: 10
        },
        plugins: {
            legend: { display: false },
            datalabels: {
                display: false
            }
        }
    };

    const trendData = useMemo(() => {
        const start = moment(appliedFilters.startDate);
        const end = moment(appliedFilters.endDate);
        const days = end.diff(start, 'days') + 1;
        
        let trend = [];
        for(let i = 0; i < days; i++) {
            const currentDay = moment(start).add(i, 'days');
            const dayStr = currentDay.format('YYYY-MM-DD');
            const isWeekend = currentDay.day() === 0 || currentDay.day() === 6;
            
            if(!isWeekend) {
                let leave = 0;
                let wfh = 0;

                baseFilteredData.forEach(req => {
                    if (moment(dayStr).isBetween(req.START_DATE, req.END_DATE, 'day', '[]')) {
                        if (req._reqType === 'Leave') leave++;
                        else if (req._reqType === 'WFH') wfh++;
                    }
                });

                trend.push({ date: dayStr, leave, wfh });
            }
        }
        return trend;
    }, [baseFilteredData, appliedFilters.startDate, appliedFilters.endDate]);

    const barChartData = {
        labels: trendData.map(d => moment(d.date).format('DD MMM')),
        datasets: [
            {
                label: '  Leave',
                data: trendData.map(d => d.leave),
                backgroundColor: '#3B82F6',
                borderRadius: 4
            },
            {
                label: '  WFH',
                data: trendData.map(d => d.wfh),
                backgroundColor: '#22C55E',
                borderRadius: 4
            }
        ]
    };

    const barChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                position: 'top', 
                align: 'end',
                labels: {
                    usePointStyle: true,
                    font: { size: 11 },
                    padding: 16
                }
            },
            datalabels: {
                display: false
            },
            tooltip: {
                mode: 'index',
                intersect: false
            }
        },
        interaction: {
            mode: 'index',
            intersect: false
        },
        scales: {
            x: { 
                grid: { display: false }, 
                ticks: { maxRotation: 0, minRotation: 0 } 
            },
            y: { 
                beginAtZero: true, 
                grid: { borderDash: [4, 4] }, 
                ticks: { stepSize: 1 },
                title: { display: true, text: 'Number of Requests', color: '#64748b', font: { size: 12, weight: '500' }, padding: { bottom: 10 } }
            }
        }
    };

    const renderTooltipCell = (params, colWidth = 150) => {
        const val = params.formattedValue !== undefined && params.formattedValue !== null ? String(params.formattedValue) : (params.value !== undefined && params.value !== null ? String(params.value) : '');
        return (
            <Tooltip title={val} placement="top" arrow>
                <div style={{ maxWidth: `${colWidth - 16}px`, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {val}
                </div>
            </Tooltip>
        );
    };

    const columns = [
        { headerName: "Emp ID", field: "EMP_ID", width: 100, renderCell: p => renderTooltipCell(p, 100) },
        { headerName: "Employee Name", field: "EMP_NAME", width: 180, renderCell: p => renderTooltipCell(p, 180) },
        { headerName: "Request Type", field: "_reqType", width: 130, 
          renderCell: params => (
              <Tooltip title={params.row._reqType} placement="top" arrow>
                  <div className="flex items-center overflow-hidden h-full">
                      <span className={`w-20 text-center py-1 rounded-full text-[11px] font-bold block ${params.row._reqType === 'Leave' ? 'bg-blue-50 text-blue-600' : 'bg-green-50 text-green-600'}`}>
                          {params.row._reqType}
                      </span>
                  </div>
              </Tooltip>
          )
        },
        { headerName: "Leave Type / WFH", field: "LEAVE_TYPE", width: 150, renderCell: p => renderTooltipCell(p, 150) },
        { headerName: "#Days / Hours", field: "NO_OF_DAYS", width: 120, 
          valueGetter: params => {
              if (params.row.NO_OF_DAYS) {
                  const numDays = Number(params.row.NO_OF_DAYS);
                  return numDays === 0.5 ? "Half Day" : `${numDays} Day${numDays > 1 ? 's' : ''}`;
              }
              const start = moment(params.row.START_DATE);
              const end = moment(params.row.END_DATE);
              if (start.isValid() && end.isValid()) {
                 const days = end.diff(start, 'days') + 1;
                 return `${days} Day${days > 1 ? 's' : ''}`;
              }
              return 'N/A';
          },
          renderCell: p => renderTooltipCell(p, 120)
        },
        { headerName: "Start Date", field: "START_DATE", width: 110, valueGetter: params => moment(params.row.START_DATE).format('DD MMM YYYY'), renderCell: p => renderTooltipCell(p, 110) },
        { headerName: "End Date", field: "END_DATE", width: 110, valueGetter: params => moment(params.row.END_DATE).format('DD MMM YYYY'), renderCell: p => renderTooltipCell(p, 110) },
        { headerName: "Reason", field: "LEAVE_REASON", width: 200, valueGetter: params => params.row.LEAVE_REASON || params.row.REASON || 'No reason provided', renderCell: p => renderTooltipCell(p, 200) },
        { headerName: "Status", field: "LEAVE_STATUS", width: 110, 
          renderCell: params => {
              const s = params.row.LEAVE_STATUS || params.row.STATUS || "Pending";
              let badge = <span className="w-[84px] text-center text-yellow-600 font-bold text-[11px] py-1 bg-yellow-50 rounded-full block">Pending</span>;
              let title = "Pending";
              
              if(s === "Approved" || s === "Accepted") {
                  badge = <span className="w-[84px] text-center text-green-600 font-bold text-[11px] py-1 bg-green-50 rounded-full block">Approved</span>;
                  title = "Approved";
              } else if(s === "Rejected" || s === "Declined") {
                  badge = <span className="w-[84px] text-center text-red-600 font-bold text-[11px] py-1 bg-red-50 rounded-full block">Rejected</span>;
                  title = "Rejected";
              }
              
              return (
                  <Tooltip title={title} placement="top" arrow>
                      <div className="flex items-center overflow-hidden h-full">
                          {badge}
                      </div>
                  </Tooltip>
              );
          } 
        },
        { headerName: "Applied On", field: "CREATED_DATE", width: 120, valueGetter: params => moment(params.row.CREATED_DATE || params.row.START_DATE).format('DD MMM YYYY'), renderCell: p => renderTooltipCell(p, 120) }
    ];

    return (
        <div className="flex flex-col h-full relative overflow-y-auto w-full space-y-3 rounded-xl">
            {/* Header / Filter Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 border-b border-slate-100 pb-5 mb-5">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                            <CalendarCheck size={20} />
                        </div>
                        <div>
                            <h2 className="text-[16px] font-bold text-slate-800 leading-tight">Time-Off & WFH Overview</h2>
                            <span className="text-[13px] text-slate-500 font-medium mt-1 block">Monitor leave and work-from-home requests across your team.</span>
                        </div>
                    </div>
                    <div className="flex flex-col items-end gap-2 shrink-0 mt-3 lg:mt-0">
                        <div className="flex items-center bg-slate-50 border border-slate-200 px-3 py-1.5 rounded-lg">
                            <span className="text-xs font-bold text-slate-700">
                                {moment(appliedFilters.startDate).format('DD MMM YYYY')} - {moment(appliedFilters.endDate).format('DD MMM YYYY')}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex flex-wrap gap-3 items-end">
                    <div className="flex-1 min-w-[140px] max-w-[250px]">
                        <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">Start Date</label>
                        <TextField 
                            type="date" 
                            size="small" 
                            fullWidth 
                            value={filters.startDate} 
                            onChange={(e) => handleFilterChange('startDate', e.target.value)} 
                            InputLabelProps={{ shrink: true }} 
                            inputProps={{ max: filters.endDate || moment().format('YYYY-MM-DD') }}
                            sx={{ '& .MuiOutlinedInput-root': { height: '40px', minHeight: '40px', fontSize: '14px' } }}
                        />
                    </div>
                    <div className="flex-1 min-w-[140px] max-w-[250px]">
                        <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">End Date</label>
                        <TextField 
                            type="date" 
                            size="small" 
                            fullWidth 
                            value={filters.endDate} 
                            onChange={(e) => handleFilterChange('endDate', e.target.value)} 
                            InputLabelProps={{ shrink: true }} 
                            inputProps={{ min: filters.startDate, max: moment().format('YYYY-MM-DD') }}
                            sx={{ '& .MuiOutlinedInput-root': { height: '40px', minHeight: '40px', fontSize: '14px' } }}
                        />
                    </div>
                    <div className="flex-1 min-w-[140px] max-w-[250px]">
                        <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">Employee</label>
                        <Autocomplete
                            multiple
                            options={employeesList.filter(e => e.STATUS === "Active")}
                            getOptionLabel={(option) => `${option.EMP_NAME} (${option.EMP_ID})`}
                            value={filters.employees}
                            onChange={(e, val) => handleFilterChange('employees', val)}
                            size="small"
                            className="w-full bg-white text-sm"
                            disableCloseOnSelect
                            renderTags={() => null} // Hides internal chips
                            ListboxProps={{ sx: { fontSize: '14px' } }}
                            renderInput={(params) => <TextField {...params} placeholder={filters.employees.length === 0 ? "All Employees" : `${filters.employees.length} Selected`} />}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    height: '40px',
                                    minHeight: '40px',
                                    paddingTop: '0 !important',
                                    paddingBottom: '0 !important',
                                    fontSize: '14px'
                                },
                                '& .MuiAutocomplete-input': {
                                    paddingTop: '0 !important',
                                    paddingBottom: '0 !important',
                                }
                            }}
                        />
                    </div>
                    <div className="flex-1 min-w-[140px] max-w-[250px]">
                        <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">Request Type</label>
                        <Select 
                            size="small" 
                            fullWidth 
                            value={filters.requestType} 
                            onChange={(e) => handleFilterChange('requestType', e.target.value)} 
                            MenuProps={selectMenuProps}
                            sx={{ 
                                height: '40px', 
                                fontSize: '14px',
                                '& .MuiSelect-select': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        >
                            <MenuItem value="All">All Types</MenuItem>
                            <MenuItem value="Leave">Leave</MenuItem>
                            <MenuItem value="WFH">WFH</MenuItem>
                        </Select>
                    </div>
                    <div className="flex-1 min-w-[140px] max-w-[250px]" style={filters.requestType === 'WFH' ? { cursor: 'not-allowed' } : {}}>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">Leave Type</label>
                        <div style={filters.requestType === 'WFH' ? { pointerEvents: 'none', opacity: 0.6 } : {}}>
                            <Select 
                                size="small" 
                                fullWidth 
                                value={filters.requestType === 'WFH' ? 'All' : filters.leaveType} 
                                onChange={(e) => handleFilterChange('leaveType', e.target.value)}
                                disabled={filters.requestType === 'WFH'}
                                MenuProps={selectMenuProps}
                                sx={{ 
                                    height: '40px', 
                                    backgroundColor: filters.requestType === 'WFH' ? '#f8fafc' : 'transparent', 
                                    fontSize: '14px',
                                    '& .MuiSelect-select': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                    }
                                }}
                            >
                                <MenuItem value="All">All Leaves</MenuItem>
                                {leaveOptions.map(leave => (
                                    <MenuItem key={leave.value} value={leave.value}>{leave.label}</MenuItem>
                                ))}
                            </Select>
                        </div>
                    </div>
                    <div className="flex-1 min-w-[140px] max-w-[250px]">
                        <label className="block text-xs font-bold text-slate-700 mb-1.5 uppercase tracking-wide">Status</label>
                        <Select 
                            size="small" 
                            fullWidth 
                            value={filters.status} 
                            onChange={(e) => handleFilterChange('status', e.target.value)} 
                            MenuProps={selectMenuProps}
                            sx={{ 
                                height: '40px', 
                                fontSize: '14px',
                                '& .MuiSelect-select': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }
                            }}
                        >
                            <MenuItem value="All">All Status</MenuItem>
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Pending">Pending</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                        </Select>
                    </div>
                    
                    <div className="flex items-end h-[40px] shrink-0">
                        <button 
                            onClick={handleApplyFilters} 
                            disabled={loading}
                            className="w-auto text-sm h-[40px] px-5 bg-blue-600 text-white rounded-[6px] hover:bg-blue-700 transition-colors font-medium shadow-sm flex items-center justify-center gap-2 whitespace-nowrap disabled:bg-blue-400 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin inline-block"></span>
                            ) : (
                                <FilterAltOutlined style={{ fontSize: 18 }} />
                            )}
                            Apply Filters
                        </button>
                    </div>
                </div>

                {filters.employees.length > 0 && (
                    <>
                        <hr className="my-5 border-slate-200" />
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                            <div className="flex flex-wrap gap-2 flex-1 items-center">
                                <span className="text-xs font-bold text-slate-500 uppercase mr-1">Selected Employees:</span>
                                {filters.employees.map((emp) => (
                                    <Chip 
                                        key={emp.EMP_CODE} 
                                        label={emp.EMP_NAME} 
                                        size="small" 
                                        className='font-semibold'
                                        onDelete={() => handleFilterChange('employees', filters.employees.filter(e => e.EMP_CODE !== emp.EMP_CODE))}
                                        deleteIcon={<X size={14} strokeWidth={2.5} />}
                                        sx={{ 
                                            backgroundColor: '#eff6ff', // blue-50 (subtle light blue)
                                            color: '#2563eb', // blue-600
                                            '& .MuiChip-deleteIcon': {
                                                color: '#3b82f6', // blue-500
                                                '&:hover': { color: '#1d4ed8' } // blue-700
                                            }
                                        }}
                                    />
                                ))}
                                <button 
                                    onClick={handleResetFilters}
                                    className="text-[12px] text-red-500 hover:text-red-700 font-medium ml-2 underline underline-offset-2"
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
            
            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {/* Doughnut: 1/3 */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 min-h-[350px] h-auto flex flex-col">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-pink-50 text-pink-600 flex items-center justify-center shrink-0">
                            <PieChart size={20} />
                        </div>
                        <div>
                            <h3 className="text-[16px] font-bold text-slate-800 m-0 leading-tight">Request Distribution</h3>
                            <span className="text-[13px] text-slate-500 font-medium mt-0.5 block">Leave vs WFH requests</span>
                        </div>
                    </div>
                    
                    {loading ? (
                        <div className="flex-1 flex items-center justify-center"><Skeleton variant="circular" width={180} height={180} /></div>
                    ) : (
                        <div className="flex flex-row flex-wrap flex-1 min-h-0 items-center justify-center gap-4 relative w-full mt-2">
                            <div className="w-[200px] h-[200px] relative shrink-0">
                                <Doughnut data={doughnutData} options={doughnutOptions} />
                                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                    <span className="text-4xl font-black text-slate-800 leading-tight">{loading ? 0 : totalReqs}</span>
                                    <span className="text-[12px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Total</span>
                                </div>
                            </div>
                            <div className="flex flex-col gap-4 flex-1">
                                {/* Leave Legend */}
                                <div className="flex flex-col border-b border-slate-100 pb-3">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-2.5 h-2.5 rounded-full bg-blue-500 shrink-0"></span>
                                        <span className="text-[13px] font-bold text-slate-700 truncate">Leave Requests</span>
                                    </div>
                                    <div className="flex items-center justify-between pl-4 gap-2">
                                        <div className="flex items-end gap-1">
                                            <span className="text-lg font-black text-slate-800 leading-none">{leaveCount}</span>
                                            <span className="text-[11px] font-medium text-slate-500">Requests</span>
                                        </div>
                                        <span className="text-[11px] font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md shrink-0">{Math.round(leaveCount/totalReqs*100) || 0}%</span>
                                    </div>
                                </div>
                                {/* WFH Legend */}
                                <div className="flex flex-col">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0"></span>
                                        <span className="text-[13px] font-bold text-slate-700 truncate">Work From Home</span>
                                    </div>
                                    <div className="flex items-center justify-between pl-4 gap-2">
                                        <div className="flex items-end gap-1">
                                            <span className="text-lg font-black text-slate-800 leading-none">{wfhCount}</span>
                                            <span className="text-[11px] font-medium text-slate-500">Requests</span>
                                        </div>
                                        <span className="text-[11px] font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-md shrink-0">{Math.round(wfhCount/totalReqs*100) || 0}%</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bar Chart: 2/3 */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5 min-h-[350px] h-auto flex flex-col">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
                            <BarChart3 size={20} />
                        </div>
                        <div>
                            <h3 className="text-[16px] font-bold text-slate-800 m-0 leading-tight">Daily Request Activity</h3>
                            <span className="text-[13px] text-slate-500 font-medium mt-0.5 block">Requests grouped by date</span>
                        </div>
                    </div>
                    
                    {loading ? (
                        <div className="flex-1 w-full"><Skeleton variant="rounded" height="100%" /></div>
                    ) : (
                        <div className="flex-1 min-h-0">
                            <Bar data={barChartData} options={barChartOptions} plugins={[ChartDataLabels]} />
                        </div>
                    )}
                </div>
            </div>
            
            {/* Request History Section */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col min-h-[400px]">
                <div className="px-5 pt-5 pb-2 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
                            <History size={20} />
                        </div>
                        <div>
                            <h3 className="text-[16px] font-bold text-slate-800 m-0 leading-tight">Request History</h3>
                            <span className="text-[13px] text-slate-500 font-medium mt-0.5 block">Detailed log of all requests</span>
                        </div>
                    </div>
                </div>
                
                <div className="flex-1 p-0 relative">
                    <CentralizedTable 
                        rows={tableData} 
                        columns={columns} 
                        state={{ isLoading: loading }}
                        getRowId={(row) => row.id}
                        autoHeight={true}
                        hideFooterSelectedRowCount
                        disableSelectionOnClick
                              muiTableContainerProps={{
                                    sx: {
                                        minHeight: "auto",
                                        height: 'auto',
                                        flex: 1,
                                        overflow: 'auto',
                                    },
                                }}
                        renderTopToolbarCustomActions={() => (
                            <div className="flex items-center h-[56px] -ml-3">
                                {['All', 'Leave', 'WFH'].map((tabValue) => {
                                    const label = tabValue === 'WFH' ? 'Work From Home' : tabValue;
                                    const isActive = activeTab === tabValue;
                                    return (
                                        <button
                                            key={tabValue}
                                            type="button"
                                            onClick={() => setActiveTab(tabValue)}
                                            className={`relative h-full px-5 text-sm font-medium transition-colors duration-200 ${
                                                isActive
                                                    ? 'text-blue-600'
                                                    : 'text-slate-500 hover:text-slate-700'
                                            }`}
                                        >
                                            {label}
                                            {isActive && (
                                                <span className="absolute bottom-0 left-0 h-[2px] w-full bg-blue-600" style={{ marginBottom: '-1px' }}></span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    />
                </div>
            </div>
        </div>
    );
}

